import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useTranslation } from '../useTranslation';

const Boutique = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [colorFilter, setColorFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // 6 produits par page

  // Fetch products from Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase.from("products").select("*");
      if (error) {
        console.error("Erreur récupération produits:", error);
      } else {
        setProducts(data);
        setFilteredProducts(data);
      }
    };

    fetchProducts();
  }, []);

  // Apply filters
  useEffect(() => {
    let result = [...products];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()))
    }

    // Apply price filter
    if (priceFilter !== "all") {
      const [min, max] = priceFilter.split("-").map(Number);
      result = result.filter(product => {
        const price = Number(product.price);
        return price >= min && price <= max;
      });
    }

    // Apply color filter
    if (colorFilter !== "all") {
      result = result.filter(product => 
        product.color && product.color.toLowerCase() === colorFilter.toLowerCase()
      );
    }

    // Apply size filter
    if (sizeFilter !== "all") {
      result = result.filter(product => 
        product.size && product.size.toLowerCase() === sizeFilter.toLowerCase()
      );
    }

    if (categoryFilter !== "all") {
      result = result.filter(product => 
        product.category && product.category.toLowerCase() === categoryFilter.toLowerCase()
      );
    }

    setFilteredProducts(result);
    setCurrentPage(1); 
  }, [products, searchTerm, priceFilter, colorFilter, sizeFilter, categoryFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePriceFilterChange = (filter) => {
    setPriceFilter(filter);
  };

  const countByCategory = (category) => {
    return products.filter(p => p.category === category).length;
  };

  return (
    <> <style>{`
    body {
      background: #0c0b09;
    }
     .pagination .page-item.active .page-link {
        background-color: #4b4f5e;
        border-color: #4b4f5e;
        color: white;
      }
      
      .pagination .page-link {
        color: #6c7293;
        background-color: #0c0b09;
        border: 1px solid #2a2d3a;
      }
      
      .pagination .page-link:hover {
        background-color: #2a2d3a;
        border-color: #2a2d3a;
        color: white;
      }
      
      .pagination .page-item.disabled .page-link {
        background-color: #191c24;
        border-color: #2a2d3a;
        color: #6c7293;
      }  
  `}</style>
      <div className="container-fluid pt-5">
        <div className="row px-xl-5">
          <div className="col-lg-3 col-md-12">
            {/* Price Filter */}
            <div className="border-bottom mb-4 pb-4">
              <h5 className="font-weight-semi-bold mb-4">{t('FilterByPrice')}</h5>
              <div className="price-filter">
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-all"
                    checked={priceFilter === "all"}
                    onChange={() => handlePriceFilterChange("all")}
                  />
                  <label className="custom-control-label" htmlFor="price-all">{t('AllPrice')}</label>
                  <span className="badge border font-weight-normal">{products.length}</span>
                </div>
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-1"
                    checked={priceFilter === "0-100"}
                    onChange={() => handlePriceFilterChange("0-100")}
                  />
                  <label className="custom-control-label" htmlFor="price-1">0 DT - 100 DT</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.price >= 0 && p.price <= 100).length}
                  </span>
                </div>
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-2"
                    checked={priceFilter === "100-200"}
                    onChange={() => handlePriceFilterChange("100-200")}
                  />
                  <label className="custom-control-label" htmlFor="price-2">100 DT - 200 DT</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.price > 100 && p.price <= 200).length}
                  </span>
                </div>
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-3"
                    checked={priceFilter === "200-300"}
                    onChange={() => handlePriceFilterChange("200-300")}
                  />
                  <label className="custom-control-label" htmlFor="price-3">200 DT - 300 DT</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.price > 200 && p.price <= 300).length}
                  </span>
                </div>
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-4"
                    checked={priceFilter === "300-400"}
                    onChange={() => handlePriceFilterChange("300-400")}
                  />
                  <label className="custom-control-label" htmlFor="price-4">300 DT - 400 DT</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.price > 300 && p.price <= 400).length}
                  </span>
                </div>
                <div className="custom-control custom-radio d-flex align-items-center justify-content-between">
                  <input 
                    type="radio" 
                    className="custom-control-input" 
                    name="price" 
                    id="price-5"
                    checked={priceFilter === "400-500"}
                    onChange={() => handlePriceFilterChange("400-500")}
                  />
                  <label className="custom-control-label" htmlFor="price-5">400 DT - 500 DT</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.price > 400 && p.price <= 500).length}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Color Filter */}
            <div className="border-bottom mb-4 pb-4">
              <h5 className="font-weight-semi-bold mb-4">{t('FilterByColor')}</h5>
              <form>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "all"}
                    onChange={() => setColorFilter("all")}
                    id="color-all"
                  />
                  <label className="custom-control-label" htmlFor="color-all">{t('AllColor')}</label>
                  <span className="badge border font-weight-normal">{products.length}</span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "black"}
                    onChange={() => setColorFilter("black")}
                    id="color-1"
                  />
                  <label className="custom-control-label" htmlFor="color-1">Black</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.color === "black").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "white"}
                    onChange={() => setColorFilter("white")}
                    id="color-2"
                  />
                  <label className="custom-control-label" htmlFor="color-2">White</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.color === "white").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "red"}
                    onChange={() => setColorFilter("red")}
                    id="color-3"
                  />
                  <label className="custom-control-label" htmlFor="color-3">Red</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.color === "red").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "blue"}
                    onChange={() => setColorFilter("blue")}
                    id="color-4"
                  />
                  <label className="custom-control-label" htmlFor="color-4">Blue</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.color === "blue").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={colorFilter === "green"}
                    onChange={() => setColorFilter("green")}
                    id="color-5"
                  />
                  <label className="custom-control-label" htmlFor="color-5">Green</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.color === "green").length}
                  </span>
                </div>
              </form>
            </div>
            
            {/* Size Filter */}
            <div className="mb-5">
              <h5 className="font-weight-semi-bold mb-4">Filter by weight</h5>
              <form>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "all"}
                    onChange={() => setSizeFilter("all")}
                    id="size-all"
                  />
                  <label className="custom-control-label" htmlFor="size-all">All Weights</label>
                  <span className="badge border font-weight-normal">{products.length}</span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "xs"}
                    onChange={() => setSizeFilter("xs")}
                    id="size-1"
                  />
                  <label className="custom-control-label" htmlFor="size-1">1 KG</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.size === "xs").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "s"}
                    onChange={() => setSizeFilter("s")}
                    id="size-2"
                  />
                  <label className="custom-control-label" htmlFor="size-2">2 KG</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.size === "s").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "m"}
                    onChange={() => setSizeFilter("m")}
                    id="size-3"
                  />
                  <label className="custom-control-label" htmlFor="size-3">3 KG</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.size === "m").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between mb-3">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "l"}
                    onChange={() => setSizeFilter("l")}
                    id="size-4"
                  />
                  <label className="custom-control-label" htmlFor="size-4">4 KG</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.size === "l").length}
                  </span>
                </div>
                <div className="custom-control custom-checkbox d-flex align-items-center justify-content-between">
                  <input 
                    type="checkbox" 
                    className="custom-control-input" 
                    checked={sizeFilter === "xl"}
                    onChange={() => setSizeFilter("xl")}
                    id="size-5"
                  />
                  <label className="custom-control-label" htmlFor="size-5">5 KG</label>
                  <span className="badge border font-weight-normal">
                    {products.filter(p => p.size === "xl").length}
                  </span>
                </div>
              </form>
            </div>
          </div>
          
          {/* Products Section */}
          <div className="col-lg-9 col-md-12">
            <div className="row pb-3">
              <div className="col-12 pb-1">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  {/* Search Bar */}
                  <div className="input-group" style={{ width: "500px" }}>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder={t('SearchProducts')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text bg-transparent text-primary">
                        <i className="bi bi-searchbi bi-search text-white"></i>
                      </span>
                    </div>
                  </div>

                  {/* Category Filter Buttons */}
                  <div className="d-flex align-items-center gap-2">
  <button 
    onClick={() => setCategoryFilter('all')}
    className={`filter-btn ${categoryFilter === 'all' ? 'active' : ''}`}
  >
    {t('allProducts')} <span className="item-count">{products.length}</span>
  </button>
  <button 
    onClick={() => setCategoryFilter('Miel')}
    className={`filter-btn ${categoryFilter === 'Miel' ? 'active' : ''}`}
  >
    {t('honeyCategory')}  <span className="item-count">{countByCategory('Miel')}</span>
  </button>
  <button 
    onClick={() => setCategoryFilter('Huile')}
    className={`filter-btn ${categoryFilter === 'Huile' ? 'active' : ''}`}
  >
    {t('oilCategory')} <span className="item-count">{countByCategory('Huile')}</span>
  </button>
  <button 
    onClick={() => setCategoryFilter('Savon')}
    className={`filter-btn ${categoryFilter === 'Savon' ? 'active' : ''}`}
  >
    {t('soapCategory')} <span className="item-count">{countByCategory('Savon')}</span>
  </button>
</div>
                </div>
              </div>

              {/* Products Display */}
              <section id="chefs" className="chefs w-100" style={{ marginTop: 10, paddingTop: 10 }}>
                <div className="container" data-aos="fade-up">
                  <div className="section-title">
                    <p>{t('productsPageTitle')}</p>
                  </div>

                  <div className="row">
                    {currentProducts.length > 0 ? (
                      currentProducts.map((product) => (
                        <div className="col-lg-4 col-md-6 mb-4" key={product.id}>
                          <div className="member" data-aos="zoom-in" data-aos-delay="200">
                            <img
                              src={product.image_url}
                              className="img-fluid"
                              alt={product.name}
                              style={{ height: "300px", objectFit: "cover", width: "100%" }}
                            />
                            <div className="member-info">
                              <div className="member-info-content">
                                <div className="social ">
                                      <Link to={`/produitDetails/${product.id}`} className="btn-details">
                                      <i className="bi bi-eye"></i> {t('voirPlus')}
                                    </Link>
                                 </div> 
                              </div>
                            </div>
                          </div>
                          <div className="text-center">
                            <h4>{product.name}</h4>
                            <div className="section-title">
                              <h6>{product.price} DT</h6>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center">
                       <p>{t('noProductsFound')}</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {filteredProducts.length > itemsPerPage && (
                    <nav aria-label="Page navigation" className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={goToPreviousPage} aria-label="Previous">
                            <span aria-hidden="true">&laquo;</span>
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => paginate(page)}>
                              {page}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={goToNextPage} aria-label="Next">
                            <span aria-hidden="true">&raquo;</span>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Boutique;